import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	getCounselorParentNotificationPreferences,
	patchCounselorParentNotificationPreferences,
} from 'api/parentCommunications'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'
import { parentContactsStore } from 'shared/stores/parentContactsStore'
import { API_NOTIFICATION_ICON_BY_CODE } from '../data/parentNotificationData'
import {
	USER_FRIENDLY_ERROR,
	toUserFriendlyError,
} from '../components/InlineErrorBanner/InlineErrorBanner'

const mapPreferenceToRule = preference => ({
	id: preference.code,
	code: preference.code,
	name: preference.name,
	description: preference.description,
	templateName: preference.template_name,
	enabled: preference.enabled !== false,
	icon: API_NOTIFICATION_ICON_BY_CODE[preference.code] || 'getting_started',
	tone: 'purple',
})

const refreshAllContacts = async () => {
	const ids = (studentsStore.students || []).map(s => s.id)
	await parentContactsStore.loadAllContacts({ force: true, studentIds: ids })
}

export const useParentNotification = () => {
	const [tab, setTab] = useState('updates')
	const [rules, setRules] = useState([])
	const [prefsLoadStatus, setPrefsLoadStatus] = useState('idle')
	const [prefsError, setPrefsError] = useState(null)
	const [togglingCode, setTogglingCode] = useState(null)
	const [contactDraft, setContactDraft] = useState(null)
	const [contactFormError, setContactFormError] = useState(null)
	const [contactSaving, setContactSaving] = useState(false)
	const [contactsLoadStatus, setContactsLoadStatus] = useState('idle')
	const [contactsListError, setContactsListError] = useState(null)

	const students = studentsStore.students || []

	const activeCount = useMemo(
		() => rules.filter(rule => rule.enabled).length,
		[rules],
	)
	const pausedCount = rules.length - activeCount
	const prefsBusy = Boolean(togglingCode) || prefsLoadStatus === 'loading'

	const loadPreferences = useCallback(async () => {
		setPrefsLoadStatus('loading')
		setPrefsError(null)
		try {
			const response = await getCounselorParentNotificationPreferences()
			const preferences = response?.data?.preferences || []
			setRules(preferences.map(mapPreferenceToRule))
			setPrefsLoadStatus('success')
		} catch (error) {
			setPrefsError(toUserFriendlyError(error))
			setPrefsLoadStatus('error')
		}
	}, [])

	useEffect(() => {
		loadPreferences()
	}, [loadPreferences])

	const loadContacts = useCallback(async () => {
		setContactsLoadStatus('loading')
		try {
			await ensureStudentsLoaded()
			await refreshAllContacts()
			setContactsListError(null)
			setContactsLoadStatus('success')
		} catch (error) {
			setContactsListError(toUserFriendlyError(error))
			setContactsLoadStatus('error')
		}
	}, [])

	useEffect(() => {
		if (tab !== 'contacts') return undefined
		let cancelled = false
		;(async () => {
			setContactsLoadStatus('loading')
			setContactsListError(null)
			try {
				await ensureStudentsLoaded()
				if (cancelled) return
				const ids = (studentsStore.students || []).map(s => s.id)
				await parentContactsStore.loadAllContacts({ studentIds: ids })
				if (!cancelled) setContactsLoadStatus('success')
			} catch (error) {
				if (!cancelled) {
					setContactsListError(toUserFriendlyError(error))
					setContactsLoadStatus('error')
				}
			}
		})()
		return () => {
			cancelled = true
		}
	}, [tab])

	const contactRows = students.map(student => {
		const contacts = parentContactsStore.getContacts(student.id)
		const gradeNumber = Number(student.grade)
		return {
			studentId: student.id,
			studentName: student.name,
			grade: student.gradeLabel || (student.grade != null ? `Grade ${student.grade}` : ''),
			gradeNumber:
				Number.isFinite(gradeNumber) && gradeNumber > 0 ? gradeNumber : null,
			avatarKey: student.avatarKey,
			contacts,
		}
	})

	const toggleRule = useCallback(async code => {
		const rule = rules.find(item => item.code === code || item.id === code)
		if (!rule || togglingCode) return
		const nextEnabled = !rule.enabled
		setTogglingCode(code)
		setPrefsError(null)
		try {
			const response = await patchCounselorParentNotificationPreferences({
				[rule.code]: nextEnabled,
			})
			const preferences = response?.data?.preferences || []
			setRules(preferences.map(mapPreferenceToRule))
		} catch (error) {
			setPrefsError(toUserFriendlyError(error))
		} finally {
			setTogglingCode(null)
		}
	}, [rules, togglingCode])

	const openAddContact = useCallback(row => {
		setContactFormError(null)
		setContactDraft({
			mode: 'add',
			studentId: row.studentId,
			studentName: row.studentName,
			grade: row.grade,
			avatarKey: row.avatarKey,
			form: {
				contact_name: '',
				email: '',
				contact_type: 'parent',
				is_primary: true,
			},
		})
	}, [])

	const openEditContact = useCallback((row, options = {}) => {
		setContactFormError(null)
		setContactDraft({
			mode: 'edit',
			studentId: row.studentId,
			studentName: row.studentName,
			grade: row.grade,
			avatarKey: row.avatarKey,
			initialContactId: options.contactId || null,
			contacts: (row.contacts || []).map(contact => ({
				...contact,
				contact_type: contact.contact_type || 'parent',
				is_primary: Boolean(contact.is_primary),
			})),
		})
	}, [])

	const closeContact = useCallback(() => {
		setContactDraft(null)
		setContactFormError(null)
		setContactSaving(false)
	}, [])

	const dismissContactsListError = useCallback(() => {
		setContactsListError(null)
	}, [])

	const dismissPrefsError = useCallback(() => {
		setPrefsError(null)
	}, [])

	const updateContactField = useCallback((contactId, field, value) => {
		setContactFormError(null)
		setContactDraft(current => {
			if (!current || current.mode !== 'edit') return current
			if (field === 'is_primary' && value) {
				return {
					...current,
					contacts: current.contacts.map(contact => ({
						...contact,
						is_primary: contact.id === contactId,
					})),
				}
			}
			return {
				...current,
				contacts: current.contacts.map(contact =>
					contact.id === contactId ? { ...contact, [field]: value } : contact,
				),
			}
		})
	}, [])

	const updateAddFormField = useCallback((field, value) => {
		setContactFormError(null)
		setContactDraft(current => {
			if (!current || current.mode !== 'add') return current
			return {
				...current,
				form: { ...(current.form || {}), [field]: value },
			}
		})
	}, [])

	const submitContact = useCallback(async event => {
		event.preventDefault()
		if (!contactDraft || contactSaving) return
		const studentId = contactDraft.studentId
		const isEdit = contactDraft.mode === 'edit'
		setContactSaving(true)
		setContactFormError(null)
		try {
			if (contactDraft.mode === 'add') {
				const form = contactDraft.form || {}
				await parentContactsStore.createContact(studentId, {
					contact_name: String(form.contact_name || '').trim(),
					email: String(form.email || '').trim(),
					contact_type: form.contact_type || 'parent',
					is_primary: Boolean(form.is_primary),
				})
				setContactDraft(null)
				setContactFormError(null)
			} else {
				for (const contact of contactDraft.contacts || []) {
					await parentContactsStore.updateContact(studentId, contact.id, {
						contact_name: String(contact.contact_name || '').trim(),
						email: String(contact.email || '').trim(),
						contact_type: contact.contact_type || 'parent',
						is_primary: Boolean(contact.is_primary),
					})
				}
				setContactDraft(null)
				setContactFormError(null)
			}
		} catch (error) {
			setContactFormError(toUserFriendlyError(error, USER_FRIENDLY_ERROR))
			if (isEdit) {
				try {
					await refreshAllContacts()
					const fresh = parentContactsStore.getContacts(studentId)
					setContactDraft(current => {
						if (!current || current.mode !== 'edit') return current
						return {
							...current,
							contacts: fresh.map(contact => ({
								...contact,
								contact_type: contact.contact_type || 'parent',
								is_primary: Boolean(contact.is_primary),
							})),
						}
					})
				} catch (refreshError) {
					toUserFriendlyError(refreshError)
				}
			}
		} finally {
			setContactSaving(false)
		}
	}, [contactDraft, contactSaving])

	const deleteContact = useCallback(
		async (contactId, options = {}) => {
			if (contactSaving || !contactId) return

			const studentId = options.studentId || contactDraft?.studentId
			const keepModalOpen = options.keepModalOpen !== false
			const isModalEdit =
				contactDraft?.mode === 'edit' &&
				String(contactDraft.studentId) === String(studentId)

			if (!studentId) return
			if (keepModalOpen && !isModalEdit && !options.studentId) return

			setContactSaving(true)
			setContactFormError(null)
			try {
				await parentContactsStore.removeContact(studentId, contactId)
				await refreshAllContacts()
				const fresh = parentContactsStore.getContacts(studentId)

				if (!keepModalOpen || !isModalEdit) {
					if (isModalEdit) setContactDraft(null)
					return
				}

				if (!fresh.length) {
					setContactDraft(null)
					return
				}

				setContactDraft(current => {
					if (!current || current.mode !== 'edit') return current
					return {
						...current,
						initialContactId: null,
						contacts: fresh.map(contact => ({
							...contact,
							contact_type: contact.contact_type || 'parent',
							is_primary: Boolean(contact.is_primary),
						})),
					}
				})
			} catch (error) {
				if (isModalEdit) {
					setContactFormError(toUserFriendlyError(error, USER_FRIENDLY_ERROR))
				} else {
					setContactsListError(toUserFriendlyError(error, USER_FRIENDLY_ERROR))
				}
			} finally {
				setContactSaving(false)
			}
		},
		[contactDraft, contactSaving],
	)

	const onImportComplete = useCallback(async () => {
		await refreshAllContacts()
	}, [])

	return {
		tab,
		setTab,
		rules,
		prefsLoadStatus,
		prefsError,
		prefsBusy,
		togglingCode,
		contactDraft,
		contactFormError,
		contactSaving,
		contactRows,
		contactsLoadStatus,
		contactsListError,
		activeCount,
		pausedCount,
		toggleRule,
		openAddContact,
		openEditContact,
		closeContact,
		updateContactField,
		updateAddFormField,
		submitContact,
		deleteContact,
		onImportComplete,
		loadContacts,
		loadPreferences,
		dismissContactsListError,
		dismissPrefsError,
	}
}

export default useParentNotification
